// Sandbox hosts (docs/plans/2026-10-03-sandbox-hosts-ontwerp.md §3): the
// Docker daemons run sandboxes start on, the installation id label (#13) and
// the admin API's wire shapes and request schemas. Browser-safe (zod only).
import { z } from 'zod'
import type { SandboxHostAdvice } from './advice.js'
import type { SandboxHostAgent } from './tunnel.js'

export * from './advice.js'
export * from './tunnel.js'

/** Docker label every object a Goblic worker creates carries (#13). */
export const INSTALL_LABEL = 'goblic.install'

/** pg-boss queue: "check this sandbox host now" (API sends, worker consumes). */
export const SANDBOX_HOST_CHECK_QUEUE = 'sandbox-host.check'
/** Same options on both sides (createQueue on API and worker). */
export const SANDBOX_HOST_CHECK_QUEUE_OPTIONS = { retryLimit: 0, expireInSeconds: 120 } as const
export interface SandboxHostCheckJob { hostId: string }

/** pg-boss queue: "scan this sandbox host and store the result" (API sends, worker consumes; ontwerp §16). */
export const SANDBOX_HOST_SCAN_QUEUE = 'sandbox-host.scan'
/** 300 s: the scan's own budget (SCAN_TIMEOUT_MS = 240 s) plus margin. Same options on both sides. */
export const SANDBOX_HOST_SCAN_QUEUE_OPTIONS = { retryLimit: 0, expireInSeconds: 300 } as const
export interface SandboxHostScanJob { hostId: string }

export const SANDBOX_HOST_KINDS = ['local', 'tcp', 'ssh', 'tunnel'] as const
export type SandboxHostKind = (typeof SANDBOX_HOST_KINDS)[number]
export type SandboxHostHealthStatus = 'unknown' | 'healthy' | 'unhealthy'

/** A host's health as the worker last saw it. */
export interface SandboxHostHealth {
  status: SandboxHostHealthStatus
  checkedAt: string | null
  error: string | null
  dockerVersion: string | null
  cpus: number | null
  memoryBytes: number | null
}

/** What the worker found on a host (sandbox_hosts.last_scan, ontwerp §16). Bytes as numbers; null = not measured. */
export interface SandboxHostScan {
  /** false = the daemon could not be reached or `docker info` failed; only `error` is meaningful then. */
  ok: boolean
  error: string | null
  /** WORKER_ID of the worker that scanned. */
  scannedBy: string
  durationMs: number
  docker: {
    serverVersion: string | null
    operatingSystem: string | null
    architecture: string | null
    rootDir: string | null
    cpus: number | null
    memoryBytes: number | null
    containersRunning: number | null
  } | null
  /** The filesystem of DockerRootDir, measured by the helper container. */
  disk: { totalBytes: number; freeBytes: number } | null
  /** Why disk is null when docker is not (bind mount refused, helper timed out, image build failed …). */
  diskError: string | null
  /** Running containers without OUR goblic.install label (other installations included). */
  foreign: {
    containers: number
    /** How many of them were inspected (capped by the worker); the limits below cover only those. */
    inspected: number
    /** Sum of HostConfig.Memory > 0. */
    memoryLimitBytes: number
    /** Sum of NanoCpus / 1e9, else CpuQuota / CpuPeriod. */
    cpuLimit: number
    /** Count with Memory = 0. */
    unlimitedMemory: number
    /** Count with neither NanoCpus nor CpuQuota. */
    unlimitedCpu: number
  } | null
  /** This installation's sandboxes on the host now (goblic.sandbox=true + our label), workload from runs.sandbox_workload. */
  own: { heavy: number; light: number; unknown: number } | null
}

/** Wire shape of one host (GET/POST/PATCH responses). Secrets are never returned, only whether they are set. */
export interface SandboxHost {
  id: string
  name: string
  kind: SandboxHostKind
  host: string | null
  port: number | null
  tlsEnabled: boolean
  sshUser: string | null
  /** For ssh: null = not pinned yet; the worker stores the first fingerprint it sees (trust on first use, §15.1 item 4). */
  sshHostFingerprint: string | null
  secrets: { tlsCa: boolean; tlsCert: boolean; tlsKey: boolean; sshPrivateKey: boolean }
  /** null = no limit on this host (only the workers' own slots apply). */
  heavySlots: number | null
  lightSlots: number | null
  enabled: boolean
  /** Accepts no new runs; running ones finish. */
  draining: boolean
  health: SandboxHostHealth
  /** Runs holding a live lease on this host right now. */
  activeRuns: { heavy: number; light: number }
  /** Last "Scan & advise" result (§16); null = never scanned. */
  lastScan: SandboxHostScan | null
  /** Also set when the scan failed, so a waiting UI knows it is done. */
  lastScanAt: string | null
  /** adviseSandboxHostSlots(lastScan, …) computed by the API; null without a successful scan. */
  advice: SandboxHostAdvice | null
  /** Tunnel agent state (installer ontwerp §7.2); null for local, tcp and ssh hosts. */
  agent: SandboxHostAgent | null
  createdAt: string
  updatedAt: string
}

/** POST /installation/sandbox-hosts with kind 'tunnel': 201 body (installer ontwerp §7.2). */
export interface CreateTunnelSandboxHostResponse {
  host: SandboxHost
  install: { command: string; expiresAt: string }
}

/** What a run shows about its host (Run.sandboxHost). */
export interface SandboxHostRef { id: string; name: string; kind: SandboxHostKind }

export interface InstallationInfo {
  installationId: string
  installLabel: typeof INSTALL_LABEL
}

const PEM = z
  .string()
  .max(65_536)
  .refine((v) => /-----BEGIN [A-Z0-9 ]+-----/.test(v), 'Paste the PEM text, including the -----BEGIN … ----- line')
/** A secret on update: absent = keep, null = clear, string = replace. */
const PEM_UPDATE = PEM.nullable().optional()
const HOSTNAME = z.string().trim().min(1).max(253).regex(/^[A-Za-z0-9.\-:\[\]]+$/, 'A host name or IP address, without scheme or port')
const PORT = z.number().int().min(1).max(65_535)
const SLOTS = z.number().int().min(0).max(1000).nullable()
const NAME = z.string().trim().min(1).max(80)
const FINGERPRINT = z.string().trim().regex(/^SHA256:[A-Za-z0-9+/]{43}$/, 'An OpenSSH SHA256 fingerprint, e.g. SHA256:abc…')

const Common = {
  name: NAME,
  heavySlots: SLOTS.optional(),
  lightSlots: SLOTS.optional(),
  enabled: z.boolean().optional(),
  draining: z.boolean().optional(),
}

/** POST /installation/sandbox-hosts. tcp, ssh and tunnel: the one local host exists from the migration.
 *  The tcp TLS rule sits on the union because zod 3's discriminatedUnion takes plain objects only. */
export const CreateSandboxHostBodySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('tcp'),
    ...Common,
    host: HOSTNAME,
    port: PORT.optional(),
    tlsEnabled: z.boolean().default(true),
    tlsCa: PEM.optional(),
    tlsCert: PEM.optional(),
    tlsKey: PEM.optional(),
  }),
  z.object({
    kind: z.literal('ssh'),
    ...Common,
    host: HOSTNAME,
    port: PORT.optional(),
    sshUser: z.string().trim().min(1).max(64),
    sshPrivateKey: PEM,
    /** Absent = trust on first use (§15.1 item 4). */
    sshHostFingerprint: FINGERPRINT.optional(),
  }),
  /** Installer host (installer ontwerp §7.2): no endpoint fields; the response carries the install command. */
  z.object({
    kind: z.literal('tunnel'),
    name: NAME,
    heavySlots: SLOTS.optional(),
    lightSlots: SLOTS.optional(),
    enabled: z.boolean().optional(),
  }).strict(),
]).superRefine((v, ctx) => {
  if (v.kind === 'tcp' && v.tlsEnabled && (!v.tlsCa || !v.tlsCert || !v.tlsKey)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tlsCa'], message: 'TLS needs the CA, client certificate and client key' })
  }
})
export type CreateSandboxHostBody = z.infer<typeof CreateSandboxHostBodySchema>

/** PATCH /installation/sandbox-hosts/:id. `kind` never changes; fields that don't fit the host's kind are a 400. */
export const UpdateSandboxHostBodySchema = z.object({
  name: NAME.optional(),
  heavySlots: SLOTS.optional(),
  lightSlots: SLOTS.optional(),
  enabled: z.boolean().optional(),
  draining: z.boolean().optional(),
  host: HOSTNAME.optional(),
  port: PORT.optional(),
  tlsEnabled: z.boolean().optional(),
  tlsCa: PEM_UPDATE,
  tlsCert: PEM_UPDATE,
  tlsKey: PEM_UPDATE,
  sshUser: z.string().trim().min(1).max(64).optional(),
  sshPrivateKey: PEM.optional(),
  /** null = forget the stored fingerprint; the worker pins the next one it sees (§15.1 item 4). */
  sshHostFingerprint: FINGERPRINT.nullable().optional(),
}).strict()
export type UpdateSandboxHostBody = z.infer<typeof UpdateSandboxHostBodySchema>

/** Fields a PATCH may set per kind (API checks; web uses it to build the form). */
export const SANDBOX_HOST_EDITABLE_FIELDS: Record<SandboxHostKind, readonly (keyof UpdateSandboxHostBody)[]> = {
  local: ['name', 'heavySlots', 'lightSlots', 'enabled', 'draining'],
  tcp: ['name', 'heavySlots', 'lightSlots', 'enabled', 'draining', 'host', 'port', 'tlsEnabled', 'tlsCa', 'tlsCert', 'tlsKey'],
  ssh: ['name', 'heavySlots', 'lightSlots', 'enabled', 'draining', 'host', 'port', 'sshUser', 'sshPrivateKey', 'sshHostFingerprint'],
  tunnel: ['name', 'heavySlots', 'lightSlots', 'enabled', 'draining'],
}

/** Default Docker port when none is given. */
export function defaultSandboxHostPort(kind: 'tcp' | 'ssh', tlsEnabled = true): number {
  return kind === 'ssh' ? 22 : tlsEnabled ? 2376 : 2375
}
